/**
 * Reads the background-color of the previous and next sibling blocks and writes them as CSS
 * variables (--prev-bg, --next-bg) on the block's outer wrapper. style.css uses those vars for
 * a split gradient that makes the neighbors' backgrounds appear to extend under the floating
 * card. Re-samples whenever the <html> class changes (dark-mode toggle).
 *
 * Resolution strategy per neighbor:
 *   1. If the neighbor matches a key in BLOCK_BG_OVERRIDES, sample that mapped element.
 *   2. Otherwise BFS into its descendants (max depth 3) and sample the first opaque element.
 *   3. Fall back to 'transparent' so the gradient silently does nothing.
 */

// Flip to true to log each sample's outcome to the console — use when the effect isn't appearing.
const DEBUG = false;

// Add entries when the default walker picks the wrong element for a given block type.
// Key: any CSS selector that matches the neighbor's outer wrapper. Value: CSS selector for the
// element whose backgroundColor to sample, or ':scope' to use the matched wrapper itself.
const BLOCK_BG_OVERRIDES = {
  '[id^="sections-media-text-columns-block"]': ':scope',
};

const BLOCK_WRAPPER_SELECTOR = '[id^="sections-floating-media-text-columns-block"]';

const TRANSPARENT_BGS = new Set(['rgba(0, 0, 0, 0)', 'transparent', '']);

window.BackgroundInjector = () => {
  return {
    prevBg: 'transparent',
    nextBg: 'transparent',
    observer: null,

    /**
     * Alpine lifecycle hook. Runs the first sample on the next tick (so CSS and layout have
     * settled) and spins up a MutationObserver to re-sample whenever the <html> class changes,
     * which is how dark-mode toggles trigger here.
     */
    init() {
      const sample = () => this.sampleBackgrounds();
      const schedule = () => setTimeout(sample, 50);

      // Initial sample immediately, then again after full page load — handles the case where
      // a theme-toggle helper reads localStorage and adds the `.dark` class AFTER Alpine has
      // already initialized this component, so the first sample catches pre-dark-mode colors.
      schedule();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', schedule, { once: true });
      }

      // Re-sample whenever <html> gains/loses the `.dark` class so user-initiated theme
      // toggles refresh the gradient.
      this.observer = new MutationObserver(schedule);
      this.observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      // Re-sample after any neighbor's background-color transition finishes — catches themes
      // that animate mode changes with a CSS transition on bg-color. The MutationObserver's
      // 50ms sample lands mid-transition and reads an interpolated or stale color, so this
      // final sample at transitionend time corrects the gradient once the animation settles.
      const onTransitionEnd = (e) => {
        if (e.propertyName === 'background-color') sample();
      };
      const wrapper = this.getBlockWrapper();
      if (wrapper) {
        const prev = wrapper.previousElementSibling;
        const next = wrapper.nextElementSibling;
        this.transitionTargets = [prev, next].filter(Boolean);
        this.onTransitionEnd = onTransitionEnd;
        this.transitionTargets.forEach((el) =>
          el.addEventListener('transitionend', onTransitionEnd),
        );
      }
    },

    /**
     * Resolves the prev/next neighbor backgrounds, writes them to reactive state, and pushes
     * them onto the block wrapper as CSS custom properties (--prev-bg / --next-bg) that the
     * stylesheet's split-gradient references.
     */
    sampleBackgrounds() {
      const wrapper = this.getBlockWrapper();
      if (!wrapper) {
        if (DEBUG) console.warn('[BackgroundInjector] No block wrapper found from', this.$root);
        return;
      }

      this.prevBg = this.resolveBg(wrapper.previousElementSibling);
      this.nextBg = this.resolveBg(wrapper.nextElementSibling);
      wrapper.style.setProperty('--prev-bg', this.prevBg);
      wrapper.style.setProperty('--next-bg', this.nextBg);

      if (DEBUG) {
        console.log('[BackgroundInjector]', {
          wrapper,
          prev: wrapper.previousElementSibling,
          next: wrapper.nextElementSibling,
          prevBg: this.prevBg,
          nextBg: this.nextBg,
        });
      }
    },

    /**
     * Walks UP from $root to find the block's outer wrapper (the [id^="sections-floating..."]
     * element). Needed because ACF or the editor may add wrapping between InnerBlocks and the
     * wp-block-* element whose siblings we actually want to sample.
     */
    getBlockWrapper() {
      return this.$root.closest(BLOCK_WRAPPER_SELECTOR) || this.$root.parentElement;
    },

    /**
     * Turns a neighbor element into a background-color string. Tries the override map first
     * (escape hatch for blocks that put their bg somewhere the walker wouldn't find), then
     * falls back to the walker, then to 'transparent' if nothing opaque is found.
     */
    resolveBg(el) {
      if (!el) return 'transparent';
      const overrideBg = this.findOverrideBg(el);
      if (overrideBg) return overrideBg;
      const found = this.walkForBg(el, 3);
      return found ? getComputedStyle(found).backgroundColor : 'transparent';
    },

    /**
     * Returns the backgroundColor of the target element named in BLOCK_BG_OVERRIDES if the
     * neighbor matches one of the map's selectors. Returns null if no override applies or the
     * matched target is transparent (so the caller can fall through to the walker).
     */
    findOverrideBg(el) {
      for (const [matcher, selector] of Object.entries(BLOCK_BG_OVERRIDES)) {
        if (!el.matches(matcher)) continue;
        const target = selector === ':scope' ? el : el.querySelector(selector);
        if (!target) continue;
        const bg = getComputedStyle(target).backgroundColor;
        if (this.isOpaque(bg)) return bg;
      }
      return null;
    },

    /**
     * BFS descent into an element's subtree looking for the first descendant with an opaque
     * background. Checks the root first, then children level-by-level up to maxDepth. Returns
     * the matching Element, or null if none is found.
     */
    walkForBg(el, maxDepth = 3) {
      if (this.isOpaque(getComputedStyle(el).backgroundColor)) return el;
      const queue = [[el, 0]];
      while (queue.length) {
        const [node, depth] = queue.shift();
        if (depth >= maxDepth) continue;
        for (const child of node.children) {
          if (this.isOpaque(getComputedStyle(child).backgroundColor)) return child;
          queue.push([child, depth + 1]);
        }
      }
      return null;
    },

    /**
     * True when the passed backgroundColor string represents a visible (non-transparent) color.
     */
    isOpaque(bg) {
      return bg && !TRANSPARENT_BGS.has(bg);
    },

    /**
     * Alpine lifecycle hook. Disconnects the MutationObserver and tears down any transitionend
     * listeners so nothing leaks when the block is torn down (SPA navigations, editor block
     * removal, etc.).
     */
    destroy() {
      if (this.observer) this.observer.disconnect();
      if (this.transitionTargets && this.onTransitionEnd) {
        this.transitionTargets.forEach((el) =>
          el.removeEventListener('transitionend', this.onTransitionEnd),
        );
      }
    },
  };
};
